import type {
  CommandVersions,
  EventVersion,
} from '@festina/life-general-types/life'
import type { CommandVersion } from '@festina/unitlink-general-types/unitlink'
import {
  dateUtils,
  isEmpty,
  notEmpty,
  type Optional,
  type OptionalArray,
} from '@festina-life/core'
import { useMemo } from 'react'

import type { AnyCommandVersion } from '../components/commandActions/types'

export type EventVersionWithCommandType<
  T extends AnyCommandVersion = AnyCommandVersion,
> = {
  commandVersion?: Optional<
    Optional<Omit<T, 'commandData'>> & {
      commandData?: Optional<T['commandData']>
    }
  >
} & EventVersion

export function isActiveEvent(event: EventVersionWithCommandType) {
  return !event.rolledBackTime && !event.commandVersion?.cancelledTime
}

export const useGetEventsWithCommand = <T extends AnyCommandVersion>(
  abstractCommandVersion: OptionalArray<T>,
): EventVersionWithCommandType<T>[] =>
  useMemo(() => {
    if (!abstractCommandVersion) {
      return []
    }

    return abstractCommandVersion
      .flatMap((commandVersion) =>
        commandVersion?.commandStepVersions?.flatMap((commandStepVersion) =>
          commandStepVersion?.eventVersions
            ?.filter(notEmpty)
            .map((eventVersion) => ({
              ...eventVersion,
              commandVersion,
            })),
        ),
      )
      .filter(notEmpty)
  }, [abstractCommandVersion])

export const useGetEvents = <T extends CommandVersion>(
  abstractCommandVersion: OptionalArray<T>,
) => {
  const eventVersionsWithCommandType: EventVersionWithCommandType<T>[] =
    useGetEventsWithCommand(abstractCommandVersion)

  return useMemo(() => {
    if (!abstractCommandVersion || abstractCommandVersion.length === 0) {
      return []
    }

    const arrangeEventVersions = (
      acc: EventVersionWithCommandType<T>[],
      unsorted: EventVersionWithCommandType<T>[],
    ): EventVersionWithCommandType<T>[] => {
      const next = unsorted.find(
        (x) => x.previousEventId === acc[acc.length - 1]?.eventId,
      )

      return next ? arrangeEventVersions([...acc, next], unsorted) : acc
    }

    const addRolledBackSteps = (
      acc: EventVersionWithCommandType<T>[],
      rolledBack: EventVersionWithCommandType<T>[],
    ): EventVersionWithCommandType<T>[] => {
      // Sorting is crucial for performance (a massive x20 boost on pathological cases)
      const remaining = rolledBack.toSorted(
        (a, b) =>
          // applyDate and createTime should never be empty, but we prefer to not crash
          (a.applyDate ?? '').localeCompare(b.applyDate ?? '') ||
          (a.createTime ?? '').localeCompare(b.createTime ?? ''),
      )

      for (const next of remaining) {
        const index = acc.findIndex((x) => {
          if (next?.applyDate === x.applyDate) {
            // Bypassing dayjs is safe on these checks (both values adhere to ISO-8601).
            // It also reduces run time by up to a massive factor of over x100, depending on data.
            return (x.createTime ?? '') < (next.createTime ?? '')
          }
          return (x.applyDate ?? '') < (next?.applyDate ?? '')
        })
        acc.splice(index, 0, next)
      }

      return acc
    }

    const activeEvents = eventVersionsWithCommandType.filter(
      (x) => !x.rolledBackTime && isEmpty(x.commandVersion?.cancelledTime),
    )

    const activeEventsWithoutLatest = activeEvents.filter(
      (x) => !!x.previousEventId,
    )

    const root =
      activeEvents.find((x) => isEmpty(x.previousEventId)) ||
      eventVersionsWithCommandType.find((x) => isEmpty(x.previousEventId))

    const rolledBackEventsWithoutLatest = eventVersionsWithCommandType.filter(
      (x) =>
        (!!x.rolledBackTime || !!x.commandVersion?.cancelledTime) &&
        notEmpty(x.previousEventId),
    )

    if (!root) {
      return []
    }

    const currentHistory = arrangeEventVersions(
      [root],
      activeEventsWithoutLatest,
    ).reverse()

    return (
      addRolledBackSteps(currentHistory, rolledBackEventsWithoutLatest) ?? []
    )
  }, [eventVersionsWithCommandType, abstractCommandVersion])
}

// If a command doesn't have commandStepVersions or no eventVersion has a commandEnded without a rollBackTime it means that the command has not ended.
export const useGetUnappliedCommands = (
  abstractCommandVersion: (AnyCommandVersion | null)[],
) =>
  useMemo(
    () =>
      abstractCommandVersion
        .reduce<AnyCommandVersion[]>((acc, commandVersion) => {
          if (commandVersion) {
            // Checking if any of the eventVersions either has a commandEnded without having rollBackTime
            const isSomeStepEnded = commandVersion?.commandStepVersions?.some(
              (stepVersion) =>
                stepVersion?.eventVersions?.some(
                  (eventVersion) =>
                    (eventVersion?.commandEnded &&
                      !eventVersion?.rolledBackTime) ||
                    commandVersion.commandPendingProcessingVersion
                      ?.cancelApprovalPending,
                ),
            )

            const isAutoCommand = commandVersion?._type.includes('AutoCommand')
            const isStepVersionPresent =
              !commandVersion?.commandStepVersions?.length
            const isNotCancelled = !commandVersion.cancelledTime
            const isApproveCancel =
              !!commandVersion.commandPendingProcessingVersion
                ?.cancelApprovalPending

            if (
              ((isStepVersionPresent || !isSomeStepEnded) &&
                !isAutoCommand &&
                isNotCancelled) ||
              isApproveCancel
            ) {
              acc.push(commandVersion)
            }
          }

          return acc
        }, [])
        // Reversing to have it in the correct view order
        .reverse(),
    [abstractCommandVersion],
  )

const EMPTY: CommandVersion[] = []

// TODO in the future this should take in parameters so that we can get unapplied and applied commands in seperate requests.
export const useFilterCommands = <C extends CommandVersion>(
  commands: Optional<CommandVersions>,
) => {
  // Memoize based on the entire commands object to prevent unnecessary recalculations
  const abstractCommandVersion = commands?.abstractCommandVersions || EMPTY

  const unappliedCommandsAndApprovalRequiredCommands = useGetUnappliedCommands(
    abstractCommandVersion,
  )
  const events = useGetEvents<C>(abstractCommandVersion as C[])

  return useMemo(
    () => ({
      unappliedCommands: [...unappliedCommandsAndApprovalRequiredCommands].sort(
        (a, b) => dateUtils.compare(b.expectedApplyDate, a.expectedApplyDate),
      ),
      events,
    }),
    [events, unappliedCommandsAndApprovalRequiredCommands],
  )
}
